import {
  locale,
  locale_BCP_tag_dictionary,
  translate_options,
  translation_variables
} from "../translate";
import { z } from "zod";
import { extra_service_ext } from "../state_management/actionTypes";
import { json } from "../translation_json";
import { couponErrorTypeEnum } from "./useCheckoutCoupon";

export const Fetch = async (url: string, options?: RequestInit) => {
  return await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...options?.headers
    }
  }).then((response) => {
    if (response.ok) {
      return response;
    } else {
      return response.json().then((body) => {
        throw { ...body, status: response.status };
      });
    }
  });
};

export const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

export const isNumber = (value: number | string | undefined | null) => {
  value = parseFloat(`${value}`);

  return !isNaN(value) && typeof value === "number";
};

export type currency = "USD" | "EUR";
export const getCurrency = (): currency => {
  return "EUR";
};

export const relu = (value: number) => {
  if (value <= 0) {
    return 0;
  } else {
    return value;
  }
};
export const calc_price_with_discount = (discount: number, price: number) => {
  return relu(price - (discount / 100) * price);
};

export const getExtras = (items: extra_service_ext[]): extra_service_ext[] => {
  const itemMap: Record<string, extra_service_ext> = {};

  items.forEach((item) => {
    const currentItem = itemMap[item.id];

    if (
      !currentItem ||
      item.price_with_discount < currentItem.price_with_discount
    ) {
      itemMap[item.id] = item;
    }
  });

  return Object.values(itemMap);
};

const fallbackTag = "en-IE";

const getTagValue = (locale: locale) =>
  locale_BCP_tag_dictionary[locale] || fallbackTag;
export const currency = (
  value: string | number,
  locale: locale,
  noDecimal: boolean = false
) => {
  value = Number(value);
  const tagValue = getTagValue(locale);

  const amount = new Intl.NumberFormat(tagValue, {
    style: "currency",
    currency: "EUR",
    ...(noDecimal
      ? {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }
      : {})
  }).format(value);

  return amount;
};

export const dateFormat = (
  date: string | Date,
  locale: locale,
  useOptions?: boolean
) => {
  const options: Intl.DateTimeFormatOptions | undefined = useOptions
    ? {
        year: "numeric",
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
        hour12: false
      }
    : undefined;

  const date_value = (() => {
    try {
      if (date) {
        const newDate = new Date(date);

        if (newDate.getTime() !== newDate.getTime()) {
          throw Error(`Invalid date!`);
        }

        return newDate;
      } else {
        return undefined;
      }
    } catch (err) {
      return undefined;
    }
  })();

  if (date_value) {
    const tagValue = getTagValue(locale);

    return new Intl.DateTimeFormat([tagValue, fallbackTag], options).format(
      date_value
    );
  } else {
    return "-";
  }
};

export const text_replace = (
  originalText: string,
  options: {
    [key in translation_variables]?: (string | number) | (string | number)[];
  }
) => {
  let count: Partial<{ [key in translation_variables]: number }> = {};
  const replacedText = originalText.replace(/\{\{([^}]+)\}\}/g, (v) => {
    const value = v as translation_variables;
    const count_value = count[value];
    const new_count = typeof count_value === "number" ? count_value + 1 : 0; // Based on "0" indexing
    count[value] = new_count;

    const replace_value = options[value];
    if (!replace_value) {
      return value;
    } else {
      const str = Array.isArray(replace_value)
        ? replace_value[new_count] || "-"
        : replace_value || "-";
      return `${str}`; // Replace with the desired values
    }
  });

  return replacedText;
};

export const getServiceLink = (locale: locale) => {
  if (locale === "lt") {
    return "paslaugos";
  } else {
    return "services";
  }
};

export const isEmail = (mail: string | null | undefined) => {
  if (!mail) return false;
  return z.string().email().safeParse(mail).success;
};

export const isMobile = (mobile: string | null | undefined) => {
  if (!mobile) {
    return false;
  } else {
    return /^[+]?[\s./0-9]*[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/g.test(mobile);
  }
};

export const coupon_error_message = (
  type: couponErrorTypeEnum | undefined,
  t: (
    originalText: keyof typeof json,
    options?: translate_options | undefined
  ) => string
): string => {
  if (type === "exhausted") {
    return t("Coupon quota has been reached");
  } else if (type === "expired") {
    return t("Coupon has expired");
  } else if (type === "not_cover") {
    return t("Coupon hasn't covered selected services");
  } else if (type === "not_exist") {
    return t("This coupon doesn’t exist");
  } else {
    return t("This coupon is not applicable.");
  }
};

export const convertToTime = (
  mins: number,
  t: (
    originalText: keyof typeof json,
    options?: translate_options | undefined
  ) => string
) => {
  const day = 24 * 60;
  if (mins >= day) {
    const remainder = mins % 60;
    const hours = Math.floor(mins / 60);

    const hourString = hours === 1 ? t("1 hour") : `${hours} ${t("hours")}`;
    const minString =
      remainder === 1 ? t("1 minute") : `${remainder} ${t("minutes")}`;
    if (remainder) {
      return `${hourString}, ${minString}`;
    } else {
      return `${hourString}`;
    }
  } else {
    const minString = mins === 1 ? t("1 minute") : `${mins} ${t("minutes")}`;

    return `${minString}`;
  }
};

export const validDate = (date: Date | string): boolean => {
  try {
    const newDate = new Date(date);

    if (newDate.getTime() !== newDate.getTime()) {
      throw Error(`Invalid date!`);
    }

    return true;
  } catch (err) {
    return false;
  }
};

export const getDate = (date: string | undefined): Date => {
  try {
    if (date) {
      const localDateStr = date.replace(/-/g, "/");
      const newDate = new Date(localDateStr);

      if (newDate.getTime() !== newDate.getTime()) {
        throw Error(`Invalid date!`);
      }

      return newDate;
    } else {
      return new Date();
    }
  } catch (err) {
    return new Date();
  }
};

export const getHTMLString = (html: string) => {
  const txt = document.createElement("textarea");
  txt.innerHTML = html;
  return txt.value.replace(/\\u003C/g, "<").replace(/\\u003E/g, ">");
};

export const stripEircodeFromAddress = (address: string | null | undefined, eircode: string | null | undefined): string => {
  let finalAddress = address || '';
  if (address && eircode && eircode.trim().length >= 7 && address.toLowerCase().includes(eircode.toLowerCase())) {
    const escapedEircode = eircode.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`\\b${escapedEircode}\\b`, 'i');
    finalAddress = address.replace(regex, '')
      .replace(/,\s*,/g, ',')
      .replace(/^,\s*/, '')
      .replace(/,\s*$/, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
  }
  return finalAddress;
};
